/**
 * Feature flags store — boolean keyed by flag name.
 *
 * @remarks
 * Source de vérité pour le champ `appCtx.flags` consommé par
 * `featureRegistry.isEnabled()` via `manifest.enabledWhen.featureFlag`.
 *
 * **Trois façons d'activer un flag** :
 *  1. **Build-time** : `VITE_FEATURE_FLAGS=foo,bar` au build → flags activés au boot.
 *  2. **Runtime / debug** : `useFeatureFlagsStore.getState().setFlag('beta-x', true)`
 *     dans la console (ou n'importe où dans le code).
 *  3. **Persisté** : la valeur survit aux reload (localStorage).
 *
 * **Quand brancher un service backend** (push depuis serveur), retirer la persist
 * et hydrater depuis une query TanStack au boot.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface FeatureFlagsState {
  flags: Record<string, boolean>;
  setFlag: (name: string, value: boolean) => void;
  setFlags: (flags: Record<string, boolean>) => void;
  clearFlag: (name: string) => void;
  reset: () => void;
}

/** Lit `VITE_FEATURE_FLAGS=a,b,c` et retourne `{ a: true, b: true, c: true }`. */
function readInitialFlags(): Record<string, boolean> {
  const csv = import.meta.env.VITE_FEATURE_FLAGS;
  if (typeof csv !== 'string' || csv.length === 0) return {};
  return Object.fromEntries(
    csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => [name, true]),
  );
}

const INITIAL_FLAGS = readInitialFlags();

const isFlagRecord = (value: unknown): value is Record<string, boolean> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every((v) => typeof v === 'boolean');

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
  devtools(
    persist(
      (set) => ({
        flags: INITIAL_FLAGS,

        setFlag: (name, value) => {
          set((state) => ({ flags: { ...state.flags, [name]: value } }));
        },

        setFlags: (flags) => {
          set((state) => ({ flags: { ...state.flags, ...flags } }));
        },

        clearFlag: (name) => {
          set((state) => {
            const { [name]: _removed, ...rest } = state.flags;
            return { flags: rest };
          });
        },

        reset: () => {
          set({ flags: INITIAL_FLAGS });
        },
      }),
      {
        name: 'saas-front-v2:feature-flags',
        version: 1,
        partialize: (state) => ({ flags: state.flags }),
        /**
         * Migration policy : si on bumpe la shape (ex: ajouter metadata par flag),
         * incrémenter `version` et ajouter une branche. Payload corrompu ou
         * version inconnue → on retourne `INITIAL_FLAGS` (l'utilisateur perd ses
         * overrides locaux mais l'app reste utilisable).
         */
        // Aucune version antérieure n'existe : `migrate` n'est atteint que sur un
        // payload d'origine inconnue — on repart des flags initiaux.
        // ⚠️ Au prochain bump, adapter AVANT ce retour en comparant avec `<` :
        // `if (oldVersion < 2) { …adapter… }`. Ne jamais tester
        // `oldVersion === <version courante>` : zustand n'appelle `migrate` que si
        // les versions diffèrent, la branche serait morte. Cascade de référence :
        // `use-layout-store`. (SAAS-903)
        migrate: () => ({ flags: INITIAL_FLAGS }),
        /**
         * Les flags persistés **complètent** ceux du build au lieu de les
         * remplacer. Avec le merge par défaut, l'objet `flags` sauvegardé écrase
         * `INITIAL_FLAGS` en bloc : un flag ajouté à `VITE_FEATURE_FLAGS` restait
         * invisible pour qui avait déjà touché à un flag en console. Un override
         * explicite (`setFlag('x', false)`) garde la priorité.
         */
        merge: (persisted, current) => {
          const saved = (persisted as Partial<FeatureFlagsState> | undefined)?.flags;
          return {
            ...current,
            flags: { ...INITIAL_FLAGS, ...(isFlagRecord(saved) ? saved : {}) },
          };
        },
      },
    ),
    { name: 'feature-flags', enabled: import.meta.env.DEV },
  ),
);
